import { useState, useRef, useEffect } from "react";
import { dispersionTexts } from "../../utils/piIduData";
import { clientPath } from "../../config/key";
import { getOpacityValue } from "../../utils/function";
import { Button } from "react-bootstrap";

const DispersionComponent = ({
  pricestepData,
  setPricestepData,
  getValueByField,
}) => {
  const [dispersionHover, setDispersionHover] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dispersionRefs = useRef([]);
  const hasInitialFocus = useRef(false);
  const containerRef = useRef(null);
  
  // Moved to constant to avoid dependency issues
  const DISPERSION_IMAGES = ["1", "2", "3", "4", "5"];
  const DISPERSION_IMAGES_LENGTH = DISPERSION_IMAGES.length;
  const yellowDispersionColor = ["#E4C608", "#E4C608", "#E4C608", "#E4C608", "#E4C608"];
  const pinkDispersionColor = ["#F4A2C8", "#DD81AB", "#C65C98", "#AC397A", "#A84C6E"];
  const blueDispersionColor = ["#299AEC", "#057FD7", "#036EBA", "#005AA1", "#004E8B"];

  // Set initial focused index based on selection
  useEffect(() => {
    const activeIndex = pricestepData?.colorDispersion === "4+" 
      ? 4 
      : parseInt(pricestepData?.colorDispersion) - 1;
    
    if (activeIndex >= 0 && activeIndex < DISPERSION_IMAGES_LENGTH) {
      setFocusedIndex(activeIndex);
    } else {
      setFocusedIndex(0);
    }
  }, [pricestepData?.colorDispersion, DISPERSION_IMAGES_LENGTH]);

  // Focus the appropriate element when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < DISPERSION_IMAGES_LENGTH) {
      dispersionRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, DISPERSION_IMAGES_LENGTH]);

  // Handle initial focus when component receives focus via tab
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (containerRef.current?.contains(e.target) && !hasInitialFocus.current) {
        hasInitialFocus.current = true;
        
        if (pricestepData?.colorDispersion) {
          const activeIndex = pricestepData.colorDispersion === "4+" 
            ? 4 
            : parseInt(pricestepData.colorDispersion) - 1;
          setFocusedIndex(activeIndex);
        } else {
          setFocusedIndex(0);
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [pricestepData?.colorDispersion]);

  const handleKeyDown = (e, index) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = Math.min(prev + 1, DISPERSION_IMAGES_LENGTH - 1);
          return nextIndex;
        });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex(prev => {
          const prevIndex = Math.max(prev - 1, 0);
          return prevIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectDispersion(index);
        setTimeout(() => {
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) {
            nextField.focus();
          }
        }, 50);
        break;
      case 'Tab':
        if (!e.shiftKey && index === DISPERSION_IMAGES_LENGTH - 1) {
          e.preventDefault();
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) nextField.focus();
        } else if (e.shiftKey && index === 0) {
          setFocusedIndex(-1);
        }
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(DISPERSION_IMAGES_LENGTH - 1);
        break;
      default:
        break;
    }
  };

  const selectDispersion = (index) => {
    setPricestepData({
      ...pricestepData,
      colorDispersion: index === 4 ? "4+" : `${index + 1}`,
    });
  };

  return (
    <div 
      className="idu-section-container" 
      role="group" 
      aria-labelledby="dispersion-label"
      ref={containerRef}
      tabIndex="-1"
    >
      <div className="idu-top-section">
        <h5 id="dispersion-label">dispersion</h5>
        <p>
          Measures the surface area of the defined {pricestepData?.color?.toLowerCase()} color
          in the face-up view.{" "}
          <span
            onClick={() => getValueByField("color Dispersion")}
            // tabIndex="0"
            data-field="color-dispersion-info"
            onKeyDown={(e) => e.key === 'Enter' && getValueByField("color Dispersion")}
          >
            Read more…
          </span>
        </p>
      </div>
      <div className="idu-main-section">
        <div className="idu-undertone-arrow-section"></div>
        <div className="idu-image-box" role="radiogroup" aria-label="Dispersion selection">
          {DISPERSION_IMAGES.map((item, index) => {
            const isActive = (pricestepData?.colorDispersion === "4+" ? "5" : pricestepData?.colorDispersion) === `${index + 1}`;
            const bgColor = pricestepData?.color === "Yellow" 
              ? yellowDispersionColor[index] 
              : pricestepData?.color === "Pink" 
                ? pinkDispersionColor[index] 
                : blueDispersionColor[index];
            
            return (
              <div className="idu-image-container" key={index}>
                <Button
                  ref={el => dispersionRefs.current[index] = el}
                  className={`idu-image ${isActive ? "active-image" : ""}`}
                  data-field={`dispersion-${index}`}
                  onClick={() => selectDispersion(index)}
                  onMouseEnter={() => setDispersionHover(index)}
                  onMouseLeave={() => setDispersionHover(null)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={() => {setFocusedIndex(index); 
                    setDispersionHover(index)}}
                  tabIndex={focusedIndex === index ? 0 : -1}
                  role="radio"
                  aria-checked={isActive}
                  aria-label={`Dispersion level ${index === 4 ? "4+" : index + 1}`}
                >
                  <img
                    src={`${clientPath}/pi-idu-images/${pricestepData?.color?.toLowerCase()}/${pricestepData?.color?.toLowerCase()}-dispersion-${item}.png`}
                    alt=""
                    aria-hidden="true"
                    style={{
                      opacity: getOpacityValue(pricestepData?.saturation),
                      zIndex: dispersionHover !== index ? 1 : 0,
                    }}
                  />
                  <img
                    src={`${clientPath}/pi-idu-images/${pricestepData?.color?.toLowerCase()}/${pricestepData?.color?.toLowerCase()}-dispersion-hover-${item}.png`}
                    alt=""
                    aria-hidden="true"
                    style={{
                      opacity: (dispersionHover === index) ? 1 : getOpacityValue(pricestepData?.saturation),
                      display: dispersionHover === index ? "block" : "none",
                      zIndex: dispersionHover === index ? 1 : 0,
                    }}
                  />
                </Button>
                <p
                  style={{
                    background: bgColor,
                    color: pricestepData?.color === "Yellow" ? "#5c6da4" : "#ffff",
                  }}
                  onClick={() => selectDispersion(index)}
                  onMouseEnter={() => setDispersionHover(index)}
                  onMouseLeave={() => setDispersionHover(null)}
                  tabIndex="-1"
                >
                  {index === 4 ? "4+" : `${index + 1}`}
                </p>
              </div>
            );
          })}
        </div>
        <div className="idu-text-box">
          <p>{dispersionTexts[dispersionHover ?? (pricestepData?.colorDispersion=== "4+" ? 4 : pricestepData?.colorDispersion - 1)]}</p>
        </div>
      </div>
      <div className="idu-bottom-section">
        {Number(pricestepData?.colorDispersion === "4+" ? 5 : pricestepData?.colorDispersion) > 2 && (
          <h6>
            <img
              src={`${clientPath}/icons/idu_info.svg`}
              height="21px"
              width="21px"
              alt="alert"
            />
            Overvaluing Visual Features Leads to Wrong Pricing!
          </h6>
        )}
      </div>
    </div>
  );
};

export default DispersionComponent;